import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, LogOut, Phone, Droplet, Cake, IdCard, Users, ShieldCheck, Pencil, Plus, Trash2, AlertCircle, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/store/authStore";
import { customerApi } from "@/lib/api";
import EditProfileDialog from "@/components/EditProfileDialog";
import DependentDialog from "@/components/DependentDialog";

function initials(name) {
  if (!name) return "U";
  return name.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3 py-3">
      <div className="h-9 w-9 rounded-xl bg-accent flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium">{value}</p>
      </div>
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const storeProfile = useAuthStore((s) => s.customerProfile);
  const setProfile = useAuthStore((s) => s.setProfile);
  const logout = useAuthStore((s) => s.logout);

  const [loading, setLoading] = useState(!storeProfile);
  const [profile, setLocalProfile] = useState(storeProfile);
  const [dependents, setDependents] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [depOpen, setDepOpen] = useState(false);
  const [editingDep, setEditingDep] = useState(null);

  const loadProfile = () => {
    if (!user?._id) return;
    customerApi.getProfile(user._id).then((res) => {
      setLocalProfile(res.data);
      setProfile(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  const loadDependents = () => {
    if (!user?._id) return;
    customerApi.getDependents(user._id).then((res) => setDependents(res.data?.dependents || [])).catch(() => {});
  };

  useEffect(() => {
    loadProfile();
    loadDependents();
  }, [user?._id]);

  const basic = profile?.basicInfo || {};
  const medical = profile?.medicalInfo || {};
  const name = basic.fullName || user?.name || "Patient";
  const allergies = Array.isArray(medical.allergies) ? medical.allergies.filter(Boolean) : [];

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  const deleteDep = async (d) => {
    if (!window.confirm(`Remove ${d.fullName || d.name}?`)) return;
    try {
      await customerApi.deleteDependent(d._id || d.id);
      setDependents((prev) => prev.filter((x) => (x._id || x.id) !== (d._id || d.id)));
      toast.success("Member removed");
    } catch (e) {
      toast.error("Could not remove member");
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl md:text-3xl font-semibold">Profile</h1>

      <Card className="rounded-3xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <Avatar className="h-16 w-16">
              <AvatarFallback className="bg-primary text-primary-foreground text-lg">{initials(name)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-lg font-semibold truncate">{name}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Phone className="h-3.5 w-3.5" /> +91 {user?.phone}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="rounded-full shrink-0" onClick={() => setEditOpen(true)} data-testid="edit-profile-button">
            <Pencil className="h-4 w-4" /> Edit
          </Button>
        </div>
      </Card>

      {loading ? (
        <Skeleton className="h-48 rounded-3xl" />
      ) : (
        <Card className="rounded-3xl p-6">
          <h2 className="font-semibold mb-1">Personal details</h2>
          <div className="divide-y divide-border">
            <Row icon={IdCard} label="Patient ID" value={profile?.patientId} />
            <Row icon={Cake} label="Date of birth" value={basic.dateOfBirth} />
            <Row icon={User} label="Gender" value={basic.gender} />
            <Row icon={Droplet} label="Blood group" value={medical.bloodGroup} />
            <Row icon={MapPin} label="City" value={basic.city} />
            {allergies.length > 0 && <Row icon={AlertCircle} label="Allergies" value={allergies.join(", ")} />}
          </div>
        </Card>
      )}

      <Card className="rounded-3xl p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" /> Family members
          </h2>
          <Button variant="ghost" size="sm" className="rounded-full text-primary" onClick={() => { setEditingDep(null); setDepOpen(true); }} data-testid="add-dependent-button">
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
        {dependents.length === 0 ? (
          <p className="text-sm text-muted-foreground">No family members added yet.</p>
        ) : (
          <div className="space-y-1">
            {dependents.map((d) => (
              <div key={d._id || d.id} className="flex items-center gap-3 py-2" data-testid={`dependent-${d._id || d.id}`}>
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-secondary text-secondary-foreground text-xs">{initials(d.fullName || d.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{d.fullName || d.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {(d.relationship || d.relation || "").toLowerCase()}
                    {d.age != null ? ` • ${d.age} yrs` : ""}
                  </p>
                </div>
                <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" onClick={() => { setEditingDep(d); setDepOpen(true); }} data-testid={`edit-dep-${d._id || d.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-destructive" onClick={() => deleteDep(d)} data-testid={`delete-dep-${d._id || d.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <button onClick={() => navigate("/consent")} className="w-full flex items-center gap-3 rounded-3xl p-4 bg-accent/50 hover:bg-accent transition-colors text-left" data-testid="manage-consent">
        <ShieldCheck className="h-5 w-5 text-primary shrink-0" />
        <span className="text-sm text-muted-foreground flex-1">Manage privacy & consent (DPDP)</span>
        <Pencil className="h-4 w-4 text-muted-foreground" />
      </button>

      <Button variant="outline" className="w-full rounded-2xl h-12 text-destructive border-destructive/30" onClick={handleLogout} data-testid="profile-logout">
        <LogOut className="h-4 w-4" /> Log out
      </Button>

      <EditProfileDialog open={editOpen} onOpenChange={setEditOpen} profile={profile} userId={user?._id} onSaved={(p) => { if (p) { setLocalProfile(p); setProfile(p); } else loadProfile(); }} />
      <DependentDialog open={depOpen} onOpenChange={setDepOpen} userId={user?._id} dependent={editingDep} onSaved={loadDependents} />
    </div>
  );
}
